import { useState, useEffect, useRef, useCallback } from "react";
import { sb, SUPABASE_URL, SUPABASE_ANON_KEY, getAuthToken } from "../lib/supabase";
import { useIsMobile } from "../lib/useIsMobile";

// Compact single-contact SMS thread + composer, for embedding in a side panel / drawer
// so you can text a lead/client without leaving the page. Reads only this contact's
// messages (RLS-scoped); sends via send-sms.
const authHeaders = () => ({ apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` });
const fmtTime = (iso) => { const d = new Date(iso); const now = new Date(); return d.toDateString() === now.toDateString() ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : d.toLocaleDateString([], { month: "short", day: "numeric" }); };

export function MiniChat({ scope, contactId, phone, showToast, height = 230 }) {
  const idKey = scope === "client" ? "client_id" : "lead_id";
  const [msgs, setMsgs] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const threadRef = useRef(null);
  const isMobile = useIsMobile();

  const load = useCallback(async () => {
    if (!contactId) return;
    try {
      const url = `${SUPABASE_URL}/rest/v1/messages?${idKey}=eq.${contactId}&order=created_at.asc&select=id,direction,body,status,created_at`;
      const d = await fetch(url, { headers: authHeaders() }).then(r => r.json());
      if (Array.isArray(d)) setMsgs(d);
    } catch { /* keep prior */ }
  }, [contactId, idKey]);

  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [load]);
  useEffect(() => { if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight; }, [msgs.length]);

  async function send() {
    const text = draft.trim();
    if (!text || !phone || sending) return;
    setSending(true);
    try {
      const r = await sb.fn("send-sms", { to: phone, body: text, [idKey]: contactId });
      if (r?.error) showToast && showToast(r.error, "error");
      else { setDraft(""); if (r?.message) setMsgs(m => [...m, r.message]); }
    } catch { showToast && showToast("Couldn't send — try again", "error"); }
    setSending(false);
  }

  if (!phone) return <div style={{ fontSize: 12.5, color: "var(--text2)", padding: 14, textAlign: "center" }}>No phone number on file — add one to text this {scope}.</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", border: "1px solid var(--border)", borderRadius: "var(--r-sm, 8px)", overflow: "hidden", background: "var(--bg)" }}>
      {/* Phones: the thread is a third nested scroller inside the lead sheet — contain its
          overscroll so reaching the end doesn't fling the sheet, and keep it short enough
          that the composer stays visible with the keyboard up. */}
      <div ref={threadRef} style={{ height: isMobile ? "min(38vh, 230px)" : height, overflowY: "auto", overscrollBehavior: "contain", WebkitOverflowScrolling: "touch", padding: 10, display: "flex", flexDirection: "column", gap: 6 }}>
        {msgs.length === 0 && <div style={{ fontSize: 12, color: "var(--text2)", textAlign: "center", margin: "auto" }}>No messages yet — say hi 👋</div>}
        {msgs.map(m => {
          const out = m.direction === "out";
          return (
            <div key={m.id} style={{ alignSelf: out ? "flex-end" : "flex-start", maxWidth: "84%", padding: "7px 10px", borderRadius: 14,
              background: out ? "var(--accent)" : "var(--surface)", color: out ? "#fff" : "var(--text)", border: out ? "none" : "1px solid var(--border)",
              borderBottomRightRadius: out ? 4 : 14, borderBottomLeftRadius: out ? 14 : 4 }}>
              <div style={{ fontSize: 12.5, lineHeight: 1.4, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{m.body}</div>
              <div style={{ fontSize: 9.5, opacity: .7, marginTop: 2, textAlign: out ? "right" : "left" }}>{fmtTime(m.created_at)}{out && m.status === "failed" ? " · failed" : ""}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "flex-end", padding: 8, borderTop: "1px solid var(--border)", background: "var(--surface)" }}>
        <textarea value={draft} onChange={e => setDraft(e.target.value)} rows={1} placeholder="Type a message…"
          onKeyDown={e => { if (isMobile) return; /* phone keyboards have no Shift+Enter — return = newline, Send button sends */ if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          style={{ flex: 1, resize: "none", maxHeight: 80, minWidth: 0, padding: "8px 11px", border: "1px solid var(--border)", borderRadius: 13, fontSize: 12.5, fontFamily: "inherit", lineHeight: 1.4, background: "var(--surface)", color: "var(--text)" }} />
        <button className="btn btn-gold btn-sm" disabled={sending || !draft.trim()} onClick={send}>{sending ? "…" : "Send"}</button>
      </div>
    </div>
  );
}
